// Profile.js
import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import supabase from "./supabaseClient";
import { useAuth } from "./AuthContext";

const DEFAULT_AVATAR = "https://cdn-icons-png.flaticon.com/512/847/847969.png";

export default function Profile() {
  const { user, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [questionnaire, setQuestionnaire] = useState(null);
  const [profilePic, setProfilePic] = useState(null);

  const isAuthenticated = !!user;

  // Consistent routes matching the dashboard layout
  const navItems = [
    { name: "Extracurriculars", link: "/admissionstoolkit" },
    { name: "Saved Activities", link: "/profile" },
  ];

  // Reusable styling classes matched exactly with the navbar items
  const baseBtnClass = "text-sm font-semibold transition-all duration-200 px-4 py-2 rounded-xl text-slate-700 hover:bg-white/70 hover:text-slate-950";
  const activeBtnClass = "text-sm font-semibold transition-all duration-200 px-4 py-2 rounded-xl bg-white text-slate-900 shadow-sm";

  // Redirect if not logged in
  useEffect(() => {
    if (!loading && !user && location.pathname === "/profile") {
      navigate("/login");
    }
  }, [user, loading, navigate, location.pathname]);

  // Fetch questionnaire (saved activities)
  useEffect(() => {
    const fetchQuestionnaire = async () => {
      if (!user) return;

      const { data, error, status } = await supabase
        .from("questionnaire")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();

      if (error && status !== 406) console.error(error);
      else setQuestionnaire(data || null);
    };

    fetchQuestionnaire();
  }, [user]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/", { replace: true });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#edf4ff] text-slate-700">
        Loading...
      </div>
    );
  }

  const savedCount = questionnaire?.saved_activities?.length || 0;

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#edf4ff] text-slate-900">

      {/* ===== BACKGROUND ===== */}

      {/* Base gradient */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_#ffffff,_#dbeafe_35%,_#c7dcff_65%,_#b5cbf4_100%)]" />

      {/* Moving shine */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="shine-overlay" />
      </div>

      {/* Floating glow orbs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="orb orb1" />
        <div className="orb orb2" />
        <div className="orb orb3" />
      </div>

      {/* Grid texture */}
      <div className="absolute inset-0 opacity-[0.04] bg-[linear-gradient(to_right,#1e3a5f_1px,transparent_1px),linear-gradient(to_bottom,#1e3a5f_1px,transparent_1px)] bg-[size:70px_70px]" />

      {/* Noise */}
      <div className="absolute inset-0 noise opacity-[0.035] mix-blend-overlay" />

      {/* ===== NAVBAR ===== */}
      <nav className="absolute top-0 left-0 right-0 z-50">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-7 flex items-center justify-between">

          {/* Logo */}
          <div
            onClick={() => navigate("/")}
            className="flex items-center gap-3 cursor-pointer"
          >
            <div className="w-9 h-9 rounded-2xl bg-[#1e3a5f] text-white flex items-center justify-center font-black shadow-lg">
              C
            </div>

            <div className="font-bold tracking-tight text-lg">
              CollegeNavi
            </div>
          </div>

          {/* Nav */}
          <div className="flex items-center gap-3 md:gap-5">

            {navItems.map((item) => {
              const active = location.pathname === item.link;

              return (
                <Link
                  key={item.name}
                  to={item.link}
                  className={active ? activeBtnClass : baseBtnClass}
                >
                  {item.name}
                </Link>
              );
            })}

            {isAuthenticated ? (
              <button
                onClick={handleLogout}
                className="text-sm font-semibold text-red-600 hover:text-red-700 px-4 py-2"
              >
                Logout
              </button>
            ) : (
              <Link
                to="/login"
                className={location.pathname === "/login" ? activeBtnClass : baseBtnClass}
              >
                Log In
              </Link>
            )}
          </div>
        </div>
      </nav>

      {/* ===== HERO TITLE SECTION ===== */}
      <section className="relative z-10 flex flex-col items-center justify-center text-center px-6 max-w-5xl mx-auto pt-40 pb-12">
        <motion.h1
          className="mt-2 text-5xl md:text-7xl font-black leading-[1.05] tracking-tight"
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55 }}
        >
          <div className="flex flex-col items-center leading-[1.05]">
            <span>Your Personal</span>
            <span className="mt-2 inline-block text-transparent bg-clip-text text-glaze-sweep">
              dashboard.
            </span>
          </div>
        </motion.h1>

        <motion.p
          className="mt-8 max-w-2xl mx-auto text-base md:text-lg leading-relaxed text-slate-600 font-medium"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.15 }}
        >
          View your saved activities and see where they fit in your academic path.
        </motion.p>
      </section>

      {/* ===== MAIN CONTENT GRID ===== */}
      <section className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-10 pb-24 flex-grow">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-start">
          
          {/* LEFT COLUMN: PROFILE CARD */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, ease: "easeOut", delay: 0.1 }}
            className="bg-white/65 backdrop-blur-2xl border border-white/60 rounded-[24px] p-6 flex flex-col items-center shadow-[0_20px_80px_rgba(30,58,95,0.06)]"
          >
            <div className="w-28 h-28 md:w-32 md:h-32 rounded-full overflow-hidden border-2 border-[#1e3a5f]/10 shadow-sm bg-white">
              <img
                src={profilePic || DEFAULT_AVATAR}
                alt="Profile"
                className="object-cover w-full h-full"
              />
            </div>

            <p className="mt-5 text-sm text-slate-800 font-semibold text-center break-all bg-white/80 border border-slate-200/50 px-4 py-1.5 rounded-full shadow-sm">
              {user?.email}
            </p>

            <div className="mt-6 w-full flex flex-col gap-3">
              <button
                type="button"
                onClick={() => navigate("/admissionstoolkit")}
                className="w-full text-sm font-semibold px-5 py-2.5 rounded-xl border border-black/30 bg-white/60 backdrop-blur-md shadow-[0_4px_20px_rgba(0,0,0,0.04)] hover:bg-white/80 hover:border-black/40 transition-all duration-200"
              >
                Browse Activities
              </button>

              <button
                type="button"
                onClick={handleLogout}
                className="w-full text-sm font-semibold px-5 py-2.5 rounded-xl border border-red-200/50 bg-red-50/60 text-red-700 hover:bg-red-100/80 transition-all duration-200"
              >
                Logout
              </button>
            </div>

            <div className="mt-5 w-full border-t border-slate-200/60 pt-4">
              <div className="flex items-center justify-between text-sm tracking-wide font-semibold">
                <span className="text-slate-500 text-xs uppercase tracking-wider">Saved Items</span>
                <span className="font-bold text-[#1e3a5f] bg-[#1e3a5f]/10 px-2.5 py-0.5 rounded-full text-xs">
                  {savedCount}
                </span>
              </div>
            </div>
          </motion.div>

          {/* RIGHT COLUMN: ACTIVITIES DISPLAY */}
          <motion.div
            id="saved-activities"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, ease: "easeOut", delay: 0.15 }}
            className="md:col-span-2 bg-white/65 backdrop-blur-2xl border border-white/60 rounded-[24px] p-6 md:p-8 shadow-[0_20px_80px_rgba(30,58,95,0.12)]"
          >
            <div className="flex items-center justify-between gap-3 border-b border-slate-200/60 pb-4">
              <h2 className="text-xl font-black tracking-tight text-slate-900">
                Saved Activities
              </h2>
              <span className="text-[11px] uppercase tracking-wider text-slate-500 font-bold bg-white/80 border border-slate-200/60 px-3 py-1 rounded-md shadow-sm">
                {savedCount} collected
              </span>
            </div>

            <div className="mt-6">
              {!questionnaire?.saved_activities?.length ? (
                <div className="text-center py-14">
                  <p className="text-sm font-medium text-slate-600">
                    No activities saved yet. Build your portfolio!
                  </p>
                  <button
                    type="button"
                    onClick={() => navigate("/admissionstoolkit")}
                    className="mt-5 text-sm font-semibold px-5 py-2.5 rounded-xl border border-black/30 bg-white/60 backdrop-blur-md shadow-[0_8px_30px_rgba(0,0,0,0.08)] hover:bg-white/80 hover:border-black/40 transition-all duration-200"
                  >
                    Find activities →
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {questionnaire.saved_activities.map((act, idx) => (
                    <div
                      key={idx}
                      className="bg-white/90 rounded-xl border border-slate-200 p-5 shadow-sm hover:shadow-md hover:border-slate-300 transition flex flex-col justify-between"
                    >
                      <p className="text-sm font-bold text-slate-900 leading-snug tracking-wide">
                        {act}
                      </p>
                      <p className="mt-3 text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
                        Active Track
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>

        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <div className="fixed bottom-5 right-5 z-50">
        <div className="flex items-center gap-3 bg-white/75 backdrop-blur-xl border border-white/70 rounded-2xl px-4 py-3 shadow-xl">
          <button
            onClick={() => navigate("/privacy")}
            className="text-xs font-medium text-slate-600 hover:text-slate-900"
          >
            Privacy
          </button>

          <div className="w-px h-4 bg-slate-300" />

          <button
            onClick={() => navigate("/public")}
            className="text-xs font-medium text-slate-600 hover:text-slate-900"
          >
            Policy
          </button>
        </div>
      </div>

      {/* ===== STYLES ===== */}
      <style>{`

        .noise {
          background-image: url("https://www.transparenttextures.com/patterns/noise.png");
        }

        /* Cleaned up Text Glaze Sweep effect using shifting backgrounds */
        .text-glaze-sweep {
          background-image: linear-gradient(
            110deg,
            #1e3a5f 35%,
            #8bb4f7 48%,
            #ffffff 50%,
            #8bb4f7 52%,
            #1e3a5f 65%
          );
          background-size: 200% 100%;
          animation: glaze 3s linear infinite;
        }

        @keyframes glaze {
          0% {
            background-position: 100% 0;
          }
          100% {
            background-position: -100% 0;
          }
        }

        /* Glow orbs */
        .orb {
          position: absolute;
          border-radius: 9999px;
          filter: blur(90px);
          opacity: 0.45;
        }

        .orb1 {
          width: 420px;
          height: 420px;
          background: #60a5fa;
          top: 8%;
          left: -5%;
          animation: float1 16s ease-in-out infinite;
        }

        .orb2 {
          width: 340px;
          height: 340px;
          background: #93c5fd;
          bottom: -8%;
          right: -2%;
          animation: float2 20s ease-in-out infinite;
        }

        .orb3 {
          width: 260px;
          height: 260px;
          background: #dbeafe;
          top: 45%;
          left: 50%;
          animation: float3 18s ease-in-out infinite;
        }

        @keyframes float1 {
          0%, 100% {
            transform: translate(0px, 0px);
          }
          50% {
            transform: translate(40px, -30px);
          }
        }

        @keyframes float2 {
          0%, 100% {
            transform: translate(0px, 0px);
          }
          50% {
            transform: translate(-30px, 30px);
          }
        }

        @keyframes float3 {
          0%, 100% {
            transform: translate(0px, 0px);
          }
          50% {
            transform: translate(20px, -25px);
          }
        }

      `}</style>
    </div>
  );
}